import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SmsIcon from "@material-ui/icons/Sms";
import MainContainer from "../../components/MainContainer";
import SubscriptionModal from "../../components/SubscriptionModal";
import SubscriptionCard from "../../components/SubscriptionCard";
import {
  meioFaturaLabel,
  periodoFaturaLabel,
  statusFatura,
} from "../../components/SubscriptionCard/subscriptionCardRules";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AEROS, stateText } from "../../styles/aerosTokens";

import moment from "moment";

// Grid da tabela — mockup Financeiro A + colunas do checkout de assinatura:
// Detalhes | Usuários | Conexões | Filas | Período | Meio | Valor | Vencimento | Status | Ação
const TABLE_GRID =
  "minmax(180px,2.2fr) 0.9fr 0.9fr 0.8fr 0.9fr 0.8fr 1.1fr 1.1fr 1.1fr 1fr";
// Abaixo disto o grid rola na horizontal dentro do card (cabeçalho e linhas
// no mesmo wrapper), em vez de perder colunas no overflow do card.
const TABLE_MIN_WIDTH = 920;

const STATUS_KEYS = {
  paid: "invoices.status.paid",
  overdue: "invoices.status.overdue",
  open: "invoices.status.open",
  canceled: "invoices.status.canceled",
};

const reducer = (state, action) => {
  if (action.type === "LOAD_INVOICES") {
    const invoices = action.payload.invoices || action.payload;
    const newInvoices = [];

    invoices.forEach((invoice) => {
      const invoiceIndex = state.findIndex((i) => i.id === invoice.id);
      if (invoiceIndex !== -1) {
        state[invoiceIndex] = invoice;
      } else {
        newInvoices.push(invoice);
      }
    });

    return [...state, ...newInvoices];
  }

  if (action.type === "UPDATE_INVOICES") {
    const invoice = action.payload;
    const invoiceIndex = state.findIndex((i) => i.id === invoice.id);

    if (invoiceIndex !== -1) {
      state[invoiceIndex] = invoice;
      return [...state];
    } else {
      return [invoice, ...state];
    }
  }

  if (action.type === "DELETE_INVOICE") {
    const invoiceId = action.payload;

    const invoiceIndex = state.findIndex((i) => i.id === invoiceId);
    if (invoiceIndex !== -1) {
      state.splice(invoiceIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    // ---- header da página (60px, título + contagem, ações à direita) ----
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 12px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"
      }`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
      margin: 0,
    },
    headerActions: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },
    smsBalance: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    smsBalanceValue: {
      color: stateText(theme, AEROS.cyan, "info"),
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    // ---- corpo da página ----
    pageBody: {
      flex: 1,
      padding: "24px 12px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    // ---- banner de fatura vencida ----
    overdueBanner: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
      borderRadius: 12,
      padding: "12px 18px",
      marginBottom: 16,
    },
    overdueBannerIcon: {
      color: stateText(theme, theme.palette.error.main, "error"),
      flexShrink: 0,
      "&.MuiSvgIcon-root": { fontSize: 20 },
    },
    overdueBannerText: {
      fontSize: 13,
      color: theme.palette.text.primary,
    },
    overdueBannerButton: {
      marginLeft: "auto",
      flexShrink: 0,
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
      color: stateText(theme, theme.palette.error.main, "error"),
      borderRadius: 9,
      padding: "6px 14px",
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.2),
      },
    },
    // ---- card da tabela em CSS grid ----
    tableCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"
      }`,
      borderRadius: 14,
      overflow: "hidden",
    },
    tableScroll: {
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    tableGrid: { minWidth: TABLE_MIN_WIDTH },
    tableHeadRow: {
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.text.disabled || theme.palette.aeros.textTertiary,
      backgroundColor: isLight
        ? "rgba(15,20,40,0.02)"
        : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"
      }`,
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      alignItems: "center",
      fontSize: 13.5,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    tableRowOverdue: {
      backgroundColor: alpha(theme.palette.error.main, 0.05),
    },
    tableCell: {
      padding: "11px 8px",
      minWidth: 0,
    },
    tableHeadCell: {
      padding: "12px 8px",
      minWidth: 0,
    },
    cellDetail: {
      paddingLeft: 20,
      fontWeight: 500,
    },
    cellCenter: {
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    cellValue: {
      textAlign: "right",
      fontWeight: 700,
    },
    cellDue: {
      color: theme.palette.text.secondary,
    },
    cellActionsHead: {
      textAlign: "right",
      paddingRight: 20,
    },
    cellActions: {
      display: "flex",
      justifyContent: "flex-end",
      paddingRight: 20,
    },
    // ---- pills de status ----
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
    },
    statusDot: {
      width: 6,
      height: 6,
      flexShrink: 0,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
    statusPillPago: {
      color: stateText(theme, theme.palette.success.main, "success"),
      backgroundColor: alpha(theme.palette.success.main, 0.1),
    },
    statusPillVencido: {
      color: stateText(theme, theme.palette.error.main, "error"),
      backgroundColor: alpha(theme.palette.error.main, 0.1),
    },
    statusPillEmAberto: {
      color: stateText(theme, theme.palette.warning.main, "warning"),
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
    },
    statusPillCancelada: {
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? "rgba(15,20,40,0.05)" : "rgba(255,255,255,0.06)",
    },
    // ---- ações da linha ----
    payButton: {
      borderRadius: 8,
      padding: "5px 14px",
      fontSize: 11.5,
      fontWeight: 700,
      minWidth: 0,
      whiteSpace: "nowrap",
    },
    paidMarker: {
      display: "inline-flex",
      alignItems: "center",
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"
      }`,
      color: theme.palette.text.disabled || theme.palette.aeros.textTertiary,
      borderRadius: 8,
      padding: "5px 14px",
      fontSize: 11.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    loadingRow: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "28px 0",
    },
  };
});

const Invoices = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam] = useState("");
  const [invoices, dispatch] = useReducer(reducer, []);
  const [storagePlans, setStoragePlans] = React.useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [saldoSms, setSaldoSms] = useState(null);
  // Incrementa para recarregar a lista (card da assinatura / socket).
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Falha silenciosa: tenant sem SMS habilitado ou endpoint indisponível
    // — nesse caso o saldo simplesmente não aparece no header.
    api
      .get("/sms/saldo")
      .then(({ data }) => setSaldoSms(data?.saldo ?? null))
      .catch(() => setSaldoSms(null));
  }, []);

  const handleOpenContactModal = (invoice) => {
    setStoragePlans(invoice);
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  const reloadInvoices = useCallback(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setReloadKey((k) => k + 1);
  }, []);

  const handleSubscribe = () => handleOpenContactModal({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchInvoices = async () => {
        try {
          const { data } = await api.get("/invoices/all", {
            params: { searchParam, pageNumber },
          });

          dispatch({ type: "LOAD_INVOICES", payload: data });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchInvoices();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadKey]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const rowStatus = (record) => statusFatura(record);

  const getStatusPillClass = (status) => {
    if (status === "paid") {
      return classes.statusPillPago;
    }
    if (status === "overdue") {
      return classes.statusPillVencido;
    }
    if (status === "canceled") {
      return classes.statusPillCancelada;
    }
    return classes.statusPillEmAberto;
  };

  const overdueInvoices = invoices
    .filter((invoice) => rowStatus(invoice) === "overdue")
    .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf());

  const handlePayOldestOverdue = () => {
    if (overdueInvoices.length > 0) {
      handleOpenContactModal(overdueInvoices[0]);
    }
  };

  const renderLimit = (value) =>
    value === null || value === undefined || value === "" ? "—" : value;

  return (
    <MainContainer>
      <SubscriptionModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        Invoice={storagePlans}
        contactId={selectedContactId}
      ></SubscriptionModal>
      <div className={classes.pageHeader}>
        <Typography component="h1" className={classes.pageTitle}>
          {i18n.t("subscription.page.title")}
        </Typography>
        <div className={classes.headerActions}>
          {saldoSms !== null && (
            <div className={classes.smsBalance}>
              {i18n.t("subscription.page.smsBalance")}{" "}
              <b className={classes.smsBalanceValue}>
                {Number(saldoSms).toLocaleString("pt-BR")} SMS
              </b>
            </div>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.gradientButton}
            startIcon={<SmsIcon />}
            onClick={() => history.push("/creditos-sms")}
          >
            {i18n.t("smsCredits.title")}
          </Button>
        </div>
      </div>
      <div className={classes.pageBody} onScroll={handleScroll}>
        {isAdmin && (
          <SubscriptionCard
            onPayInvoice={handleOpenContactModal}
            onSubscribe={handleSubscribe}
            onChanged={reloadInvoices}
          />
        )}
        {overdueInvoices.length > 0 && (
          <div className={classes.overdueBanner}>
            <ErrorOutlineIcon className={classes.overdueBannerIcon} />
            <div className={classes.overdueBannerText}>
              <b>
                {overdueInvoices.length === 1
                  ? "1 fatura vencida."
                  : `${overdueInvoices.length} faturas vencidas.`}
              </b>{" "}
              Regularize para evitar a suspensão do painel.
            </div>
            <Button
              className={classes.overdueBannerButton}
              onClick={handlePayOldestOverdue}
            >
              Pagar agora
            </Button>
          </div>
        )}
        <div className={classes.tableCard}>
          <div className={classes.tableScroll}>
          <div className={classes.tableGrid}>
          <div className={classes.tableHeadRow}>
            <div
              className={classes.tableHeadCell}
              style={{ paddingLeft: 20 }}
            >
              {i18n.t("invoices.table.details")}
            </div>
            <div
              className={classes.tableHeadCell}
              style={{ textAlign: "center" }}
            >
              {i18n.t("invoices.table.users")}
            </div>
            <div
              className={classes.tableHeadCell}
              style={{ textAlign: "center" }}
            >
              {i18n.t("invoices.table.connections")}
            </div>
            <div
              className={classes.tableHeadCell}
              style={{ textAlign: "center" }}
            >
              {i18n.t("invoices.table.queue")}
            </div>
            <div className={classes.tableHeadCell}>
              {i18n.t("invoices.table.period")}
            </div>
            <div className={classes.tableHeadCell}>
              {i18n.t("invoices.table.method")}
            </div>
            <div
              className={classes.tableHeadCell}
              style={{ textAlign: "right" }}
            >
              {i18n.t("invoices.table.value")}
            </div>
            <div className={classes.tableHeadCell}>
              {i18n.t("invoices.table.expirationDate")}
            </div>
            <div className={classes.tableHeadCell}>
              {i18n.t("invoices.table.status")}
            </div>
            <div
              className={`${classes.tableHeadCell} ${classes.cellActionsHead}`}
            >
              {i18n.t("invoices.table.action")}
            </div>
          </div>
          {invoices.map((invoice) => {
            const status = rowStatus(invoice);
            return (
              <div
                key={invoice.id}
                className={`${classes.tableRow} ${
                  status === "overdue" ? classes.tableRowOverdue : ""
                }`}
              >
                <div className={`${classes.tableCell} ${classes.cellDetail}`}>
                  {invoice.detail}
                </div>
                <div className={`${classes.tableCell} ${classes.cellCenter}`}>
                  {renderLimit(invoice.users)}
                </div>
                <div className={`${classes.tableCell} ${classes.cellCenter}`}>
                  {renderLimit(invoice.connections)}
                </div>
                <div className={`${classes.tableCell} ${classes.cellCenter}`}>
                  {renderLimit(invoice.queues)}
                </div>
                <div className={`${classes.tableCell} ${classes.cellDue}`}>
                  {periodoFaturaLabel(invoice)}
                </div>
                <div className={`${classes.tableCell} ${classes.cellDue}`}>
                  {meioFaturaLabel(invoice)}
                </div>
                <div className={`${classes.tableCell} ${classes.cellValue}`}>
                  {Number(invoice.value || 0).toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </div>
                <div className={`${classes.tableCell} ${classes.cellDue}`}>
                  {moment(invoice.dueDate).format("DD/MM/YYYY")}
                </div>
                <div className={classes.tableCell}>
                  <span
                    className={`${classes.statusPill} ${getStatusPillClass(
                      status
                    )}`}
                  >
                    <span className={classes.statusDot} />
                    {i18n.t(STATUS_KEYS[status])}
                  </span>
                </div>
                <div className={`${classes.tableCell} ${classes.cellActions}`}>
                  {status === "canceled" ? (
                    <span className={classes.paidMarker} aria-hidden="true">
                      —
                    </span>
                  ) : status !== "paid" ? (
                    <Button
                      variant="contained"
                      color="primary"
                      className={classes.payButton}
                      onClick={() => handleOpenContactModal(invoice)}
                    >
                      {i18n.t("invoices.table.pay")}
                    </Button>
                  ) : (
                    <span className={classes.paidMarker}>
                      {i18n.t("invoices.status.paid")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className={classes.loadingRow}>
              <CircularProgress size={24} />
            </div>
          )}
          </div>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Invoices;
